import axios from 'axios'
import dayjs from "dayjs";
import { useFormik } from 'formik'
import { enqueueSnackbar } from "notistack";
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { API, IssueStatus, TimeUnitsIds } from "../../../helpers/constants";
import { validateEndDateIsBeforeStartDate } from "../../../helpers/dates";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import { isUUID } from "../../../helpers/helpers";
import {
	edit_issue_actual_start,
	edit_issue_estimated_end,
	issue_actual_start,
	issue_estimated_end
} from "../../../helpers/rights/rights";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { useHasPermission } from "../../permission/hooks/useHasPermission";
import { ISSUES_FORM } from '../locales/namespaces'

export const calculateDuration = (start, end, dateFormat = 'DD-MM-YYYY', t, checkIfBefore = true) => {
	if (start && end) {
		const startDate = dayjs(start, dateFormat)
		const endDate = dayjs(end, dateFormat)
		
		if (startDate.isSame(endDate, 'day')) {
			return 1
		}
		
		if (endDate.isValid() && checkIfBefore && endDate.isBefore(startDate)) {
			enqueueSnackbar(t('Start date can\'t be later than end date!'), {
				variant: 'warning',
				autoHideDuration: 3000,
			})
			return 0
		}
		return endDate.diff(startDate, 'days') + 1
	}
	return 0
};

const useIssueForm = ({
						  IssueId, issue, staticData, onSuccess = () => {
	}, assignedType, assignedTypeId, Copy
					  }) => {
	const { t } = useTranslation(ISSUES_FORM)
	const { dateFormat, token, isLoggedIn, timeFormat } = useUserData();
	const { userLogout } = useUserAction()
	const [loadingSubmit, setLoadingSubmit] = useState(false)
	const hasEditIssueActualStart = useHasPermission([edit_issue_actual_start], { shouldView: true, shouldEdit: true })
	const hasIssueActualStart = useHasPermission([issue_actual_start], { shouldView: true, shouldEdit: true })
	const hasEditIssueEstimDue = useHasPermission([edit_issue_estimated_end], { shouldView: true, shouldEdit: true })
	const hasIssueEstimDue = useHasPermission([issue_estimated_end], { shouldView: true, shouldEdit: true })
	
	const onSubmit = (values) => {
		setLoadingSubmit(true)
		let url = `${API}/en/issues/store`;
		if (!IssueId && !Copy) {
			url = `${API}/en/issues/store`;
		} else if (IssueId && !Copy) {
			url = `${API}/en/issues/update/${IssueId}`
		} else if (IssueId && Copy) {
			url = `${API}/en/issues/store`;
		}
		let alertMsg = 'Issue created successfully!';
		if (!IssueId && !Copy) {
			alertMsg = 'Issue created successfully!';
		} else if (IssueId && !Copy) {
			alertMsg = 'Issue updated successfully!';
		} else if (IssueId && Copy) {
			alertMsg = 'Issue copy successfully!';
		}
		const notes = values.Notes.filter(f => f.Body.trim() && !f.isDisabled)?.map((note) => ({
			...(note.id && !isUUID(note.id) && { id: Number(note.id) }),
			Body: note.Body
		}))
		const InternalNotes = values.InternalNotes.filter(f => f.Body.trim() && !f.isDisabled)?.map((note) => ({
			...(note.id && !isUUID(note.id) && { id: Number(note.id) }),
			Body: note.Body
		}))
		if (values.CancelComment && values.CancelComment.trim() !== '') {
			notes.push({
				Body: values.CancelComment
			})
		}
		if (values.CancelInternalComment && values.CancelInternalComment.trim() !== '') {
			InternalNotes.push({
				Body: values.CancelComment
			})
		}
		axios({
			method: 'post',
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
			data: {
				ErpCode2: values.ErpCode2,
				CPOCode: values.CPOCode,
				Code: values.Code,
				IssueId: values.IssueId,
				Description: values.Description,
				isActive: values.isActive,
				Summary: values.Summary,
				Creator: values.Creator,
				isBillable: values.isBillable,
				isOnBehalf: values.isOnBehalf,
				AssigneeId: values.AssigneeId?.id,
				isNotSla: values.isNotSla,
				// TeamId: values.TeamId,
				CustomerId: values.CustomerId?.id,
				ContactId: values.ContactId?.id,
				ContactNameText: values.ContactNameText,
				ItemId: values.ItemId?.id, //service
				ItemCategId: values.ItemCategId, //service category
				AddressId: values.AddressId.id,
				Site: values.Site.id,
				StatusId: values.StatusId,
				PriorityId: values.PriorityId,
				TypeId: values.TypeId,
				Type2Id: values.Type2?.id,
				SLAId: values.SLAId,
				KindId: values.KindId,
				CaseId: (assignedType === 'Case') ? assignedTypeId : null,
				EstimDueDt: values.EstimDueDt ? dayjs(values.EstimDueDt).format('YYYY-MM-DD') : null,
				EstimDueTm: values.EstimDueTm ? dayjs(values.EstimDueTm).format('HH:mm:ss') : null,
				EstimStartDt: values.EstimStartDt ? dayjs(values.EstimStartDt).format('YYYY-MM-DD') : null,
				EstimStartTm: values.EstimStartTm ? dayjs(values.EstimStartTm).format('HH:mm:ss') : null,
				ActualVisitDt: values.ActualVisitDt ? dayjs(values.ActualVisitDt).format('YYYY-MM-DD') : null,
				ActualVisitTm: values.ActualVisitTm ? dayjs(values.ActualVisitTm).format('HH:mm:ss') : null,
				ActualStartDt: values.ActualStartDt ? dayjs(values.ActualStartDt).format('YYYY-MM-DD HH:mm:ss') : null,
				ActualEndDt: values.ActualEndDt ? dayjs(values.ActualEndDt).format('YYYY-MM-DD HH:mm:ss') : null,
				SubmitDt: values.SubmitDt ? dayjs(values.SubmitDt).format('YYYY-MM-DD HH:mm:ss') : null,
				EstimWork: values.EstimWork,
				ProposedWork: values.ProposedWork,
				EstimCost: values.EstimCost,
				ChargeCost: values.ChargeCost,
				ActualCost: values.ActualCost,
				EstimDuration: values.EstimDuration,
				SerialNumber: values.SerialNumber,
				convertEstimWork: values.convertEstimWork,
				convertPropWork: values.convertPropWork,
				convertEstimDuration: values.convertEstimDuration,
				Items: values.items.map((item) => ({
					...(!isUUID(item.id) && { id: Number(item.id) }),
					ItemId: item.ItemId?.id ? item.ItemId?.id : '',
					ItemDescription: item.ItemDescription,
					Qty: item.Qty ? parseFloat(item.Qty) : 0,
					Price: item.ItemPrice ? parseFloat(item.ItemPrice) : 0,
				})),
				Assets: values.assets.map((i) => ({
					...(!isUUID(i.id) && { id: Number(i.id) }),
					AssetId: i.asset?.id ? i.asset?.id : ''
				}))?.filter(f => f.AssetId),
				Notes: notes,
				InternalNotes:InternalNotes,
				files: values.files.filter(f => !f.id),
		IssueTechs: values.TechnicianId ? values.TechnicianId.map(tech => ({
			id: tech.id,
			Description: tech.Description
		})) : [],
			InternalAssignmentId: values.InternalAssignmentId?.id ?? null,
			InternalAssignmentTeamId: values.InternalAssignmentTeamId?.id ?? null,
			},
			url: url
		}).then(response => {
			if (response?.status === 202 && response?.data?.message) {
				enqueueSnackbar(t(response.data.message), {
					variant: 'warning',
					autoHideDuration: 3000,
				})
				setLoadingSubmit(false)
			} else {
				enqueueSnackbar(t(`${alertMsg}`), {
					variant: 'success',
					autoHideDuration: 3000,
				})
				setLoadingSubmit(false)
				onSuccess({ formik })
			}
		}).catch((error) => {
			setLoadingSubmit(false)
			const errorMessage = `${t('An error occurred!')} ${error?.response?.data?.errors}`;
			enqueueSnackbar(handleAxiosErrors(error, errorMessage), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}
	
	//έλεγχος για τα items, αν σε κάθε ανοιχτή γραμμή υπάρχει μία από τις δύο τιμές ItemId, ItemDescription αλλιώς να βγάλει σφάλμα και να μην αφήσει να κάνει submit.
	const itemSchema = yup.object().shape({
		ItemId: yup.mixed(), //στην αρχή δηλώνουμε τον τύπο των μεταβλητών χωρίς να τα κάνουμε required
		ItemDescription: yup.string(),
	}).test( //ξεκινάει το τεστ
		'itemId-or-description-required',
		t('Either Item or Description is required'),
		function (value) { // φτιάχνουμε μια συνάρτηση που παίρνει ολόκληρο το item (τη γραμμή)
			const { ItemId, ItemDescription } = value; // από όλο το value παίρνουμε μόνο τα 2 πεδία που χρειαζόμαστε για τον έλεγχο
			const hasItemId = ItemId && (typeof ItemId === 'object'
				? !!ItemId.id || !!ItemId.Description
				: !!ItemId); //ελέγχουμε αν υπάρχει ItemId, αν υπάρχει βγάζει true
			const hasDescription = ItemDescription && ItemDescription.trim() !== ''; //ελέγχουμε αν υπάρχει ItemDescription και αν υπάρχει να μην έχει μονο κενά.
			if (!hasItemId && !hasDescription) {
				// Εμφανίζει το error στο ItemId
				return this.createError({ path: `${this.path}.ItemId` });
			}
			return true;
		}
	);
	const validationSchema = yup.object().shape({
		Description: yup.string().max(250, t('Enter no more than 250 characters.')).required(t('Required')),
		CustomerId: yup.mixed().test(
			'acceptedValues', t('Required'), function (value) {
				return value;
			}
		),
		AddressId: yup.mixed().test(
			'acceptedValues', t('Required'), function (value) {
				return value;
			}
		),
		ActualCost: yup.number()
		.max(99999999.99, t('Max allowed value 99999999.99')),
		ChargeCost: yup.number()
		.max(99999999.99, t('Max allowed value 99999999.99')),
		items: yup.array().of(itemSchema), //περνάμε εντός του validation τον έλεγχο που φτιάξαμε παραπάνω.
		CancelComment: yup.mixed().test(
			'acceptedValues', t('Required'), function (value) {
				const { requireCancelComment } = this.parent;
				return !(requireCancelComment && !value);
			}),
		EstimStartDt: yup.mixed()
		.test('is-before-end', t("Start date can't be later than end date!"), function (value) {
			const { EstimDueDt, EstimStartTm, EstimDueTm } = this.parent
			// Κάνε validation για σύγκριση start - end μόνο αν ο χρήστης έχει δικαίωμα επεξεργασίας του πεδίου due
			return !(hasEditIssueEstimDue || hasIssueEstimDue) ? true : validateEndDateIsBeforeStartDate(value, EstimDueDt, EstimStartTm, EstimDueTm)
		})
		.test('is-valid', t("Invalid date"), function (value) {
			return value ? dayjs(value)?.isValid() : true
		})
		.nullable(),
		EstimDueDt: yup.mixed().test(
			'acceptedValues', t('Invalid date'), function (value) {
				return value ? value?.isValid() : true
			}
		).nullable(),
		// Δεν μπορεί να το πειράξει από τη φόρμα οπότε το απενεργοποίησα για τώρα ----------
		ActualVisitDt: yup.mixed()
		// .test('is-before-end', t("Start date can't be later than end date!"), function (value) {
		// 	const { TargetedEndDt, TargetedStartTm, TargetedEndTm } = this.parent
		// 	return validateEndDateIsBeforeStartDate(value, TargetedEndDt, TargetedStartTm, TargetedEndTm)
		// })
		.test('is-valid', t("Invalid date"), function (value) {
			return value ? dayjs(value)?.isValid() : true
		})
		.nullable(),
		// Δεν μπορεί να το πειράξει από τη φόρμα οπότε το απενεργοποίησα για τώρα ----------
		// TargetedEndDt: yup.mixed().test(
		// 	'acceptedValues', t('Invalid date'), function (value) {
		// 		return value ? value?.isValid() : true
		// 	}
		// ).nullable(),
		ActualStartDt: yup.mixed()
		.test('is-before-end', t("Start date can't be later than end date!"), function (value) {
			const { ActualEndDt } = this.parent
			return validateEndDateIsBeforeStartDate(value, ActualEndDt)
		})
		.test('is-valid', t("Invalid date or time"), function (value) {
			return value ? dayjs(value)?.isValid() : true
		})
		// TODO::Ενεργοποίησέ το πάλι όταν ζητήσουν να γίνει υποχρεωτική η συμπλήρωσή του! Είπαν στην πρώτη φάση να μην είναι.
		// .test('is-required-upon-completion', t("Required"), function (value) {
		// 	const { StatusId } = this.parent
		// 	const requireActualStart = StatusId && Number(StatusId) === IssueStatus.Epilithike && (hasEditIssueActualStart || hasIssueActualStart)
		// 	return !(requireActualStart && !value);
		// })
		.nullable(),
		ActualEndDt: yup.mixed().test(
			'acceptedValues', t('Invalid date or time'), function (value) {
				return value ? value?.isValid() : true
			}
		).nullable(),
		EstimDueTm: yup.mixed().test(
			'acceptedValues', t('Invalid time'), function (value) {
				return value ? value?.isValid() : true
			}
		).nullable(),
		EstimStartTm: yup.mixed().test(
			'acceptedValues', t('Invalid time'), function (value) {
				return value ? value?.isValid() : true
			}
		).nullable(),
		ActualVisitTm: yup.mixed().test(
			'acceptedValues', t('Invalid time'), function (value) {
				return value ? value?.isValid() : true
			}
		).nullable(),
		// Δεν μπορεί να το πειράξει από τη φόρμα οπότε το απενεργοποίησα για τώρα ----------
		// TargetedStartTm: yup.mixed().test(
		// 	'acceptedValues', t('Invalid time'), function (value) {
		// 		return value ? value?.isValid() : true
		// 	}
		// ).nullable(),
	})

	
	const formik = useFormik({
		initialValues: {
			Code: issue?.Code || '',
			ErpCode: issue?.ErpCode || '',
			ErpCode2: issue?.ErpCode2 || '',
			CPOCode: issue?.CPOCode || '',
			Description: issue?.Description || '',
			isActive: IssueId ? issue?.isActive : true,
			searchForAddress: true,
			isBillable: IssueId ? issue?.isBillable : true,
			isOnBehalf: IssueId ? issue?.isOnBehalf : true,
			isNotSla: IssueId ? issue?.isNotSla : true,
			Summary: issue?.Summary || '',
			Creator: issue?.Creator || '',
			AssigneeId: issue?.AssigneeId ? issue.AssigneeId : '',
			CustomerId: issue?.CustomerId ? issue.CustomerId : '',
			TechnicianId: issue?.IssueTechs ? issue.IssueTechs.map(tech => ({
				id: tech.id,
				Description: tech.Description || tech.TechName || tech.Name || ''
			})) : [],
			//InternalAssignmentId: issue?.InternalAssignmentId ? { id: issue.InternalAssignmentId, Description: issue.InternalAssignment } : '',
			InternalAssignmentId : issue?.InternalAssignmentId
				? (staticData.teamUsers ?? []).find(
				teamUser => String(teamUser.id) === String(issue.InternalAssignmentId)
			) || null : null,
			InternalAssignmentTeamId: issue?.InternalAssignmentTeamId
				? (staticData.teams ?? []).find(
				team => String(team.id) === String(issue.InternalAssignmentTeamId)
			) || null : null,
			ContactId: issue?.ContactId ? issue.ContactId : '',
			ContactNameText: issue?.ContactNameText || '',
			CategoryItemId: issue?.ItemCategId && issue?.ItemCategDescription ? {
				id: issue?.ItemCategId,
				Description: issue?.ItemCategDescription
			} : '',
			ItemId: issue?.ItemId && issue?.ItemDescription ? {
				id: issue?.ItemId,
				Description: issue?.ItemDescription
			} : '',
			StatusId: issue?.StatusId || '',
			PriorityId: issue?.PriorityId || '',
			KindId: issue?.KindId || '',
			TypeId: issue?.TypeId || '',
			Type2: issue?.Type2 ? issue?.Type2 : '',
			AddressId: issue?.AddressId ? issue?.AddressId : '',
			Site: issue?.AddressId && issue?.Site ? { id: issue?.AddressId, Description: issue?.Site } : '',
			PrjId: issue?.PrjId && issue?.PrjDescription ? {
				id: issue?.PrjId,
				Description: issue?.PrjDescription
			} : '',
			SLAId: issue?.SLAId || '',
			SubmitDt: issue?.SubmitDt ? dayjs(issue.SubmitDt) : null,
			ActualStartDt: issue?.ActualStartDt ? dayjs(issue.ActualStartDt) : null,
			ActualEndDt: issue?.ActualEndDt ? dayjs(issue.ActualEndDt) : issue?.ActualStartDt ? dayjs(issue.ActualStartDt) : null,
			EstimDueDt: issue?.EstimDueDt ? dayjs(issue.EstimDueDt) : null,
			EstimDueTm: issue?.EstimDueTm ? dayjs(issue.EstimDueTm, 'HH:mm:ss') : null,
			EstimStartDt: issue?.EstimStartDt ? dayjs(issue.EstimStartDt) : null,
			EstimStartTm: issue?.EstimStartTm ? dayjs(issue.EstimStartTm, 'HH:mm:ss') : null,
			TargetedEndDt: issue?.TargetedEndDt ? dayjs(issue.TargetedEndDt) : null,
			DateReg: issue?.DateReg ? dayjs(issue.DateReg) : dayjs(),
			TargetedEndTm: issue?.TargetedEndTm ? dayjs(issue.TargetedEndTm, 'HH:mm:ss') : null,
			TargetedStartDt: issue?.TargetedStartDt ? dayjs(issue.TargetedStartDt) : null,
			TargetedStartTm: issue?.TargetedStartTm ? dayjs(issue.TargetedStartTm, 'HH:mm:ss') : null,
			ActualVisitDt: issue?.ActualVisitDt ? dayjs(issue.ActualVisitDt) : null,
			ActualVisitTm: issue?.ActualVisitTm ? dayjs(issue.ActualVisitTm, 'HH:mm:ss') : null,
			EstimWork: issue?.EstimWork ? (issue.EstimWork / 480) : 0,
			ProposedWork: issue?.ProposedWork ? (issue.ProposedWork / 480) : 0,
			EstimCost: issue?.EstimCost || 0,
			ChargeCost: issue?.ChargeCost || 0,
			ActualCost: issue?.ActualCost || 0,
			SerialNumber: issue?.SerialNumber || '',
			EstimDuration: issue?.EstimDuration ? (issue.EstimDuration / 480) : 0,
			convertEstimWork: TimeUnitsIds.Days,
			convertPropWork: TimeUnitsIds.Days,
			convertEstimDuration: TimeUnitsIds.Days,
			items: issue && issue?.Items && issue.Items.length > 0 ? issue.Items.map(item => ({
				id: item.id,
				ItemId: item.ItemId && item.Description ? { id: item.ItemId, Description: item.Description } : '',
				ItemDescription: item.ItemDescription || '',
				Qty: item?.Qty ? item.Qty : 0,
				ItemPrice: item?.Price ? parseFloat(item.Price) : 0,
				ItemValue: item?.Qty * parseFloat(item?.Price),
			})) : [],
			assets: issue && issue?.Assets && issue.Assets?.length > 0 ? issue.Assets.map(asset => ({
				id: asset.id,
				asset: asset.AssetId && asset.Description ? { id: asset.AssetId, Description: asset.Description } : '',
			})) : [],
			Notes: issue?.Notes && issue.Notes.length > 0 ? issue.Notes.map(note => ({
				...note,
				LastModifiedAt: note.LastModifiedAt ? dayjs(note.LastModifiedAt).format(`${dateFormat} ${timeFormat}`) : '',
				isDisabled: true
			})) : [],
			InternalNotes: issue?.InternalNotes && issue.InternalNotes.length > 0 ? issue.InternalNotes.map(note => ({
				...note,
				LastModifiedAt: note.LastModifiedAt ? dayjs(note.LastModifiedAt).format(`${dateFormat} ${timeFormat}`) : '',
				isDisabled: true
			})) : [],
			files: issue?.files || [],
			CancelComment: '',
			CancelInternalComment: '',
			requireCancelComment: false
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	
	return {
		formik,
		calculateDuration,
		loadingSubmit
	}
	
}
export default useIssueForm